<template>
  <div class="member-page">
    <div class="form-wrapper">
      <ul class="nav nav-tabs justify-content-center mb-4">
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'login' }"
            href="#"
            @click.prevent="activeTab = 'login'"
          >{{ $t('member.Login') }}</a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'register' }"
            href="#"
            @click.prevent="activeTab = 'register'"
          >{{ $t('member.Register') }}</a>
        </li>
      </ul>

      <!-- Login -->
      <div v-if="activeTab === 'login'">
        <h2 class="text-center mb-4">{{ $t('member.Login') }}</h2>

        <div v-if="loginState.error" class="alert alert-danger">
          {{ loginState.error }}
          <div v-if="loginState.unverified" class="mt-2">
            <button
              class="btn btn-outline-primary btn-sm"
              :disabled="loginState.resending || emailResend.secondsLeft > 0"
              @click="onResendVerification"
            >
              <span v-if="loginState.resending" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('member.resend') }}
              <span v-if="emailResend.secondsLeft > 0">({{ emailResend.secondsLeft }}s)</span>
            </button>
          </div>
        </div>

        <!-- reset 訊息 -->
        <div v-if="resetState.success" class="alert alert-success">{{ resetState.success }}</div>
        <div v-if="resetState.error" class="alert alert-danger">{{ resetState.error }}</div>

        <form class="mt-4" style="max-width: 400px; margin: 0 auto;" @submit.prevent="onLogin">
          <div class="mb-3">
            <label class="form-label">{{ $t('member.EmailPhone') }}</label>
            <input
              v-model.trim="loginForm.identifier"
              type="text"
              class="form-control"
              required
              autocomplete="username"
              :placeholder="$t('member.inputeEailPhone')"
            />
          </div>
          <div class="mb-3">
            <label class="form-label">{{ $t('member.Password') }}</label>
            <input v-model="loginForm.password" type="password" class="form-control" required autocomplete="current-password" />
          </div>
          <button type="submit" class="btn btn-primary w-100" :disabled="loginState.loading">
            <span v-if="loginState.loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ loginState.loading ? $t('common.processing') : $t('member.Login') }}
          </button>

          <div class="text-center mt-3">
            <button type="button" class="btn btn-link p-0" :disabled="resetState.sending" @click="onSendReset">
              <span v-if="resetState.sending" class="spinner-border spinner-border-sm me-1"></span>
              {{$t('member.forget')}}
            </button>
          </div>
        </form>
      </div>

      <!-- Register -->
      <div v-else>
        <h2 class="text-center mb-4">{{ $t('member.Register') }}</h2>

        <div v-if="registerState.success" class="alert alert-success">{{ registerState.success }}</div>
        <div v-if="registerState.error" class="alert alert-danger">{{ registerState.error }}</div>

        <div class="register-switch" role="group" aria-label="register-type">
          <button
            type="button"
            class="btn"
            :class="registerMode === 'email' ? 'btn-primary' : 'btn-outline-primary'"
            @click="registerMode = 'email'"
          >
            {{ $t('member.registerEmail') }}
          </button>

          <button
            type="button"
            class="btn"
            :class="registerMode === 'phone' ? 'btn-primary' : 'btn-outline-primary'"
            @click="registerMode = 'phone'"
          >
            {{ $t('member.registerPhoneOtp') }}
          </button>
        </div>

        <!-- Email 註冊（保留） -->
        <form
          v-if="registerMode === 'email'"
          class="mt-4"
          style="max-width: 400px; margin: 0 auto;"
          @submit.prevent="onRegisterEmailAskTerms"
        >
          <div class="mb-3">
            <label class="form-label">{{$t("member.Email")}}</label>
            <input v-model.trim="form.email" type="email" class="form-control" required />
          </div>
          <div class="mb-3">
            <label class="form-label">{{$t("member.Password")}}</label>
            <input v-model="form.password" type="password" class="form-control" required minlength="8" />
          </div>
          <div class="mb-3">
            <label class="form-label">{{$t("member.configPassword")}}</label>
            <input v-model="form.confirm" type="password" class="form-control" required />
          </div>
          <button
            type="submit"
            class="btn btn-primary w-100"
            :disabled="registerState.loading || emailSignup.secondsLeft > 0"
          >
            <span v-if="registerState.loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ registerState.loading ? $t('common.processing') : $t('member.sendEmail') }}
            <span v-if="emailSignup.secondsLeft > 0">({{ emailSignup.secondsLeft }}s)</span>
          </button>
        </form>

        <!-- Phone OTP 註冊 -->
        <div v-if="registerMode === 'phone'" class="mt-4" style="max-width: 420px; margin: 0 auto;">
          <div class="mb-3">
            <label class="form-label">{{ $t('member.Phone') || '手機' }}</label>
            <input
              v-model.trim="phoneForm.phone"
              type="tel"
              class="form-control"
              required
              placeholder=""
              autocomplete="tel"
            />
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('member.Password') }}</label>
            <input
              v-model="phoneForm.password"
              type="password"
              class="form-control"
              required
              minlength="8"
              autocomplete="new-password"
            />
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('member.configPassword') }}</label>
            <input
              v-model="phoneForm.confirm"
              type="password"
              class="form-control"
              required
              minlength="8"
              autocomplete="new-password"
            />
          </div>

          <button
            type="button"
            class="btn btn-primary w-100"
            :disabled="phoneState.sending || phoneOtp.secondsLeft > 0"
            @click="onRegisterPhoneSendOtpAskTerms"
          >
            <span v-if="phoneState.sending" class="spinner-border spinner-border-sm me-2"></span>
            {{ phoneState.sending ? $t('common.processing') : ($t('member.sendOtp') || '發送驗證碼') }}
            <span v-if="phoneOtp.secondsLeft > 0">({{ phoneOtp.secondsLeft }}s)</span>
          </button>
          <div v-if="phoneState.success" class="alert alert-success mt-3">{{ phoneState.success }}</div>
        </div>
      </div>
    </div>

    <div class="decor">
      <img src="@/assets/images/mainpage.png" alt="product" />
    </div>

    <!-- ✅ Success 提示 Modal（共用：用 loginState.success 當文字） -->
    <div v-if="successModal.show" class="success-modal" @click.self="closeSuccessModal">
      <div class="success-card">
        <h3 class="mb-2">{{ $t('common.success') || '成功' }}</h3>
        <div class="success-text">{{ loginState.success }}</div>

        <div class="d-flex justify-content-end mt-3">
          <button class="btn btn-primary" @click="closeSuccessModal">
            {{ $t('common.ok') || '確定' }}
          </button>
        </div>
      </div>
    </div>


    <!-- Reset 密碼 Modal（保留） -->
    <div v-if="resetState.showModal" class="reset-modal" @click.self.stop="noop">
      <div class="reset-card">
        <h3 class="mb-3">{{ $t('member.setnewPassword') }}</h3>

        <div v-if="resetState.modalError" class="alert alert-danger">{{ resetState.modalError }}</div>

        <div class="mb-3">
          <label class="form-label" for="npw">{{ $t('member.newPassword') }}</label>
          <input
            id="npw"
            v-model="resetForm.password"
            type="password"
            class="form-control"
            minlength="8"
            :placeholder="$t('passwordUpdate.minLength')"
          />
        </div>

        <div class="mb-3">
          <label class="form-label" for="cpw">{{ $t('member.configNewPassword') }}</label>
          <input
            id="cpw"
            v-model="resetForm.confirm"
            type="password"
            class="form-control"
            minlength="8"
          />
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-primary" :disabled="resetState.updating" @click="onConfirmNewPassword">
            <span v-if="resetState.updating" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('member.configChange') }}
          </button>
          <button class="btn btn-outline-secondary" @click="closeResetModal">
            {{ $t('common.cancel') }}
          </button>
        </div>
      </div>
    </div>


    <!-- OTP 驗證 Modal -->
    <div v-if="otpModal.show" class="otp-modal" @click.self="closeOtpModal">
      <div class="otp-card">
        <h3 class="mb-3">
          {{
            otpModal.purpose === 'reset_login'
              ? ($t('member.otpLogin') || '簡訊驗證登入')
              : ($t('member.otpVerify') || '輸入簡訊驗證碼')
          }}
        </h3>


        <div v-if="otpModal.error" class="alert alert-danger">{{ otpModal.error }}</div>

        <div class="mb-3">
          <label class="form-label">{{ $t('member.otpCode') || '驗證碼' }}</label>
          <input
            v-model.trim="otpModal.code"
            type="text"
            inputmode="numeric"
            class="form-control"
            placeholder="xxxxxx"
            maxlength="6"
          />
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-primary" :disabled="otpModal.verifying" @click="onVerifyPhoneOtp">
            <span v-if="otpModal.verifying" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('member.verify') || '驗證' }}
          </button>

          <button
            class="btn btn-outline-secondary"
            :disabled="otpModal.resending || phoneOtp.secondsLeft > 0"
            @click="onResendPhoneOtp"
          >
            <span v-if="otpModal.resending" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('member.resend') }}
            <span v-if="phoneOtp.secondsLeft > 0">({{ phoneOtp.secondsLeft }}s)</span>
          </button>


          <button class="btn btn-outline-danger ms-auto" @click="closeOtpModal">
            {{ $t('common.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ✅ 同意條款 Modal（共用：Email註冊 / 手機OTP註冊 / 也可套用 resend） -->
    <div v-if="termsModal.show" class="terms-modal" @click.self="closeTermsModal">
      <div class="terms-card">
        <h3 class="terms-title">{{ $t('member.Privacy') }}</h3>

        <div class="terms-box">
          <TermsContent />
        </div>

        <div class="terms-checks">
          <label class="chk">
            <input type="checkbox" v-model="termsModal.agreePolicy" />
            <span>{{ $t('member.termsAgreePolicy') }}</span>
          </label>

          <label class="chk">
            <input type="checkbox" v-model="termsModal.isMedical" />
            <span>{{ $t('member.termsIsMedical') }}</span>
          </label>

          <div v-if="termsModal.error" class="alert alert-danger mt-2 mb-0">
            {{ termsModal.error }}
          </div>
        </div>

        <div class="terms-actions">
          <button
            class="btn btn-primary terms-btn"
            :disabled="!canConfirmTerms"
            @click="confirmTermsAndRun"
          >
            {{ $t('common.confirm') }}
          </button>

          <button
            class="btn btn-secondary terms-btn"
            @click="closeTermsModal"
          >
            {{ $t('common.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ⭐ 會員條款連結 ⭐ -->
    <div class="legal-foot" role="note" :aria-label="$t('member.privacyLinkLabel')">
      {{ $t('member.loginConfigAgree') }}
      <a
        class="legal-link"
        :href="termsHref"
        target="_blank"
        rel="noopener"
      >
        《{{ $t('member.Privacy') }}》
      </a>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'
import TermsContent from '@/views/TermsContent.vue'

export default {
  name: 'Member',
  data() {
    return {
      termsModal: {
        show: false,
        agreePolicy: false,
        isMedical: false,
        error: '',
        pendingAction: null, // 用來記住「確認後要做的原本動作」
      },

      activeTab: 'login',

      // login
      loginForm: { identifier: '', password: '' },
      loginState: { loading: false, error: '', success: '', unverified: false, resending: false },

      // register
      registerMode: 'email', // ✅ 新增：email | phone
      form: { email: '', password: '', confirm: '' },
      registerState: { loading: false, error: '', success: '' },

      // phone register (OTP)
      phoneForm: { phone: '', password: '', confirm: '' },
      phoneState: { sending: false, error: '', success: '', lastPhoneE164: '' },

      otpModal: {
        show: false,
        code: '',
        verifying: false,
        resending: false,
        error: '',
        purpose: 'register',          // ✅ 'register' | 'reset_login'
        phoneE164: ''                 // ✅ 這次 OTP 對應的手機（避免混用）
      },


      successModal: { show: false },

      // reset
      resetForm: { password: '', confirm: '' },
      resetState: { sending: false, success: '', error: '', showModal: false, updating: false, modalError: '' },

      authSub: null,

      emailResend: { key: '', secondsLeft: 0, timer: null }, // Login resend email
      emailSignup: { key: '', secondsLeft: 0, timer: null }, // Register email signUp
      phoneOtp:    { key: '', secondsLeft: 0, timer: null }, // Phone send/resend OTP
    }
  },

  components: {
    TermsContent
  },

  computed: {
    termsHref() {
      // 由路由物件安全生成可點擊的網址
      return this.$router.resolve({ name: 'TermsPrivacy' }).href
    },
    canConfirmTerms() {
      return !!(this.termsModal.agreePolicy && this.termsModal.isMedical)
    }
  },


  async mounted() {
    this.authSub = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        sessionStorage.setItem('recovery_hold', '1')
        this.activeTab = 'login'
        this.resetState.showModal = true
      }
    }).data.subscription

    const handled = await this.handleEmailRedirectResult()
    if (handled) return

    if (sessionStorage.getItem('recovery_hold') === '1') {
      this.activeTab = 'login'
      this.resetState.showModal = true
      return
    }

    // ✅ 如果網址帶有 auth redirect 的參數（code / token / access_token…）
    // 就先不要因為 session 存在而跳 /app，讓 handleEmailRedirectResult / onAuthStateChange 有機會開 modal
    const q2 = new URLSearchParams(window.location.search)
    const h2 = new URLSearchParams((window.location.hash || '').replace(/^#/, ''))
    const urlHasAuthParams =
      q2.has('code') || q2.has('token') || q2.has('token_hash') ||
      h2.has('code') || h2.has('token') || h2.has('token_hash') ||
      h2.has('access_token') || h2.has('refresh_token') || h2.has('type')

    if (!urlHasAuthParams) {
      const { data: sess } = await supabase.auth.getSession()
      if (sess?.session) {
        this.$router.replace('/app')
        return
      }
    }

    // 初始化 cooldown（依目前輸入的 email/phone；輸入改變時再 load 也行）
    this._cdLoad('emailResend', this.loginForm.identifier)
    this._cdLoad('emailSignup', this.form.email)
    this._cdLoad('phoneOtp', this.phoneState.lastPhoneE164 || this.phoneForm.phone)
  },

  beforeUnmount() {
    this.authSub?.unsubscribe?.();
    this._cdStopAll()
  },

  methods: {
    openTermsModal(action) {
      // action 是一個 function：確認後要執行的原本流程
      this.termsModal.show = true
      this.termsModal.agreePolicy = false
      this.termsModal.isMedical = false
      this.termsModal.error = ''
      this.termsModal.pendingAction = typeof action === 'function' ? action : null
    },

    closeTermsModal() {
      this.termsModal.show = false
      this.termsModal.error = ''
      this.termsModal.pendingAction = null
    },

    async confirmTermsAndRun() {
      this.termsModal.error = ''

      if (!this.termsModal.agreePolicy || !this.termsModal.isMedical) {
        this.termsModal.error = '請先勾選同意條款與身分確認，才能繼續。'
        return
      }

      const action = this.termsModal.pendingAction
      this.closeTermsModal()

      // ✅ 關閉視窗後，執行原本動作
      if (action) await action()
    },

    // ===== Cooldown helpers (persist across refresh) =====
    _cdStorageKey(name, identity) {
      // identity: email 或 phone（用來做到每個帳號各自冷卻）
      const v = (identity || '').trim().toLowerCase()
      return `mg_cd_${name}_${v}`
    },

    _cdNow() {
      return Date.now()
    },

    _cdGetRemainingSeconds(storageKey, cooldownMs = 60000) {
      const last = parseInt(localStorage.getItem(storageKey) || '0', 10)
      if (!last) return 0
      const remain = cooldownMs - (this._cdNow() - last)
      return remain > 0 ? Math.ceil(remain / 1000) : 0
    },

    _cdStart(name, identity, cooldownMs = 60000) {
      const storageKey = this._cdStorageKey(name, identity)
      localStorage.setItem(storageKey, String(this._cdNow()))

      const slot = this[name]  // ✅ 改這行
      if (!slot) return
      slot.key = storageKey

      if (slot.timer) clearInterval(slot.timer)
      slot.secondsLeft = this._cdGetRemainingSeconds(storageKey, cooldownMs)

      slot.timer = setInterval(() => {
        slot.secondsLeft = this._cdGetRemainingSeconds(storageKey, cooldownMs)
        if (slot.secondsLeft <= 0) {
          clearInterval(slot.timer)
          slot.timer = null
        }
      }, 250)
    },

    _cdLoad(name, identity, cooldownMs = 60000) {
      const storageKey = this._cdStorageKey(name, identity)
      const remain = this._cdGetRemainingSeconds(storageKey, cooldownMs)

      const slot = this[name]  // ✅ 改這行
      if (!slot) return
      slot.key = storageKey

      if (slot.timer) clearInterval(slot.timer)
      slot.secondsLeft = remain

      if (remain > 0) {
        slot.timer = setInterval(() => {
          slot.secondsLeft = this._cdGetRemainingSeconds(storageKey, cooldownMs)
          if (slot.secondsLeft <= 0) {
            clearInterval(slot.timer)
            slot.timer = null
          }
        }, 250)
      }
    },

    _cdCanSend(name, identity, cooldownMs = 60000) {
      const storageKey = this._cdStorageKey(name, identity)
      return this._cdGetRemainingSeconds(storageKey, cooldownMs) <= 0
    },

    _cdStopAll() {
      ['emailResend', 'emailSignup', 'phoneOtp'].forEach(k => {
        const s = this[k]
        if (s?.timer) clearInterval(s.timer)
        if (s) s.timer = null
      })
    },

    noop(){},
    // ===== Helpers =====
    isEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) },
    toE164Phone(input) {
      // 仍保留給「登入」使用（可用手機登入）
      let v = (input || '').replace(/\s|-/g, '')
      if (!v) return ''
      if (v.startsWith('+')) return v
      if (/^09\d{8}$/.test(v)) return '+886' + v.slice(1)
      return v
    },

    // ===== 錯誤訊息 =====
    translateSupabaseError(message) {
      if (!message) return this.$t('loginState.errorTryLater')

      const msg = message.toLowerCase()

      // 密碼或帳號錯誤（Invalid login credentials）
      if (msg.includes('invalid login credentials')) {
        return this.$t('loginState.error')   // → "登入失敗，請檢查帳號密碼是否正確"
      }

      // 尚未驗證 email
      if (msg.includes('email not confirmed') || msg.includes('confirm')) {
        return this.$t('loginState.unverified')   // → "您的帳號尚未驗證..."
      }

      // 手機格式不對（Supabase 端可能回 Invalid phone number）
      if (msg.includes('invalid phone') || msg.includes('phone')) {
        return this.$t('loginState.invalidPhone')
      }

      // Token 過期、連結無效
      if (msg.includes('expired') || msg.includes('token')) {
        return this.$t('resetState.invalidOrExpiredLink') 
          || this.$t('loginState.errorTryLater')
      }

      // 其他未知錯誤走 fallback，讓開發者能看原文
      return message
    },

    // ===== Login =====
    async onLogin() {
      this.loginState.error = ''
      this.loginState.success = ''
      this.loginState.unverified = false

      const { identifier, password } = this.loginForm
      if (!identifier || !password) {
        this.loginState.error = this.$t('loginState.inputRequired')
        return
      }

      this.loginState.loading = true
      try {
        let res, error
        if (this.isEmail(identifier)) {
          ({ data: res, error } = await supabase.auth.signInWithPassword({
            email: identifier,
            password
          }))
        } else {
          const phone = this.toE164Phone(identifier)
          if (!phone.startsWith('+')) {
            throw new Error(this.$t('loginState.invalidPhone'))
          }
          ({ data: res, error } = await supabase.auth.signInWithPassword({
            phone,
            password
          }))
        }

        if (error) {
          // 判斷是否為“尚未驗證”
          const fp = `${error.message || ''} ${error.status || ''} ${error.name || ''}`.toLowerCase()
          if (fp.includes('confirm') || fp.includes('not confirmed') || fp.includes('email_not_confirmed')) {
            this.loginState.error = this.$t('loginState.unverified')
            this.loginState.unverified = true
            return
          }
          throw error
        }

        const user = res?.user
        // 防呆：若是信箱登入且未驗證
        if (user && user.email && !user.email_confirmed_at) {
          this.loginState.error = this.$t('loginState.unverified')
          this.loginState.unverified = true
          await supabase.auth.signOut()
          return
        }

        this.loginState.success = this.$t('loginState.success')

        // ★★★ 每日唯一登入紀錄 ★★★
        try {
          const userId = res.user.id

          const todayStart = new Date()
          todayStart.setHours(0, 0, 0, 0)

          const { data: existing } = await supabase
            .from('login_logs')
            .select('id')
            .eq('user_id', userId)
            .gte('logged_at', todayStart.toISOString())
            .limit(1)

          if (!existing || existing.length === 0) {
            await supabase.from('login_logs').insert({
              user_id: userId,
              device: navigator.userAgent || null
            })
          }
        } catch (e) {
          console.warn('login log failed:', e)
        }

        this.$router.replace('/app')

      } catch (err) {
        this.loginState.error = this.translateSupabaseError(err.message)
      } finally {
        this.loginState.loading = false
      }
    },

    async onResendVerification() {
      this.loginState.error = ''
      this.loginState.success = ''
      this.loginState.resending = true
      try {
        const email = this.isEmail(this.loginForm.identifier) ? this.loginForm.identifier.trim() : ''
        if (!email) {
          this.loginState.error = this.$t('loginState.resendInputEmailFirst')
          return
        }

        const redirectToBase = `${window.location.origin}${this.$route?.fullPath || '/member'}`
        const redirectTo = this._buildRedirectTo(redirectToBase, { flow: 'signup' })
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: { emailRedirectTo: redirectTo }
        })
        if (error) throw error

        // 沿用既有 i18n key
        this.loginState.success = this.$t('loginState.resent')
        this._cdStart('emailResend', email)
      } catch (err) {
        // 後端訊息可讀就顯示，否則用本地化預設錯誤
        this.loginState.error =
          (typeof err?.message === 'string' && err.message) || this.$t('loginState.resendError')
      } finally {
        this.loginState.resending = false
      }
    },

    onRegisterEmailAskTerms() {
      this.openTermsModal(() => this._doRegisterEmail())
    },

    // ===== Register (Email) =====
    async _doRegisterEmail() {
      this.registerState.error = ''
      this.registerState.success = ''

      const email = (this.form.email || '').trim()
      const password = (this.form.password || '').trim()
      const confirm = (this.form.confirm || '').trim()

      if (password !== confirm) {
        this.registerState.error = this.$t('registerState.mismatch')
        return
      }
      if (!email || !password) {
        this.registerState.error = this.$t('registerState.formIncomplete')
        return
      }

      if (this.emailSignup?.secondsLeft > 0) return

      this.registerState.loading = true
      try {
        // ✅ Email 唯一性檢查
        const { data: available, error: chkErr } = await supabase
          .rpc('check_email_available', { p_email: email.toLowerCase() })

        if (chkErr) throw chkErr
        if (!available) {
          this.registerState.error = this.$t('registerState.emailAlreadyUsed')
          return
        }

        const redirectToBase = `${window.location.origin}${this.$route?.fullPath || '/member'}`
        const redirectTo = this._buildRedirectTo(redirectToBase, { flow: 'signup' })
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: redirectTo }
        })
        if (error) throw error

        // ✅ 用 loginState.success 當顯眼提示文字（你要的）
        this.registerState.success = '' // 不用在 register 區塊顯示 alert 了
        this.openSuccessModal(this.$t('registerState.success'))

        this._cdStart('emailSignup', email)
        this.form = { email: '', password: '', confirm: '' }
      } catch (err) {
        this.registerState.error =
          (typeof err?.message === 'string' && err.message) ||
          this.$t('registerState.error')
      } finally {
        this.registerState.loading = false
      }
    },

    onRegisterPhoneSendOtpAskTerms() {
      this.openTermsModal(() => this._doRegisterPhoneSendOtp())
    },

    // ===== Register (Phone OTP) Step 1: send OTP（解法 A）=====
    async _doRegisterPhoneSendOtp() {
      this.phoneState.error = ''
      this.phoneState.success = ''

      const phoneRaw = (this.phoneForm.phone || '').trim()
      const password = (this.phoneForm.password || '').trim()
      const confirm = (this.phoneForm.confirm || '').trim()

      if (!phoneRaw || !password) {
        this.phoneState.error = this.$t('registerState.formIncomplete')
        return
      }
      if (password.length < 8) {
        this.phoneState.error = this.$t('passwordUpdate.minLength')
        return
      }
      if (password !== confirm) {
        this.phoneState.error = this.$t('registerState.mismatch')
        return
      }

      const phone = this.toE164Phone(phoneRaw)
      if (!phone.startsWith('+')) {
        this.phoneState.error = this.$t('loginState.invalidPhone')
        return
      }

      if (this.phoneOtp?.secondsLeft > 0) return

      this.phoneState.sending = true
      try {
        // ✅ Phone 唯一性檢查
        const { data: available, error: chkErr } = await supabase
          .rpc('check_phone_available', { p_phone: phone })

        if (chkErr) throw chkErr
        if (!available) {
          this.registerState.error = this.$t('registerState.phoneAlreadyUsed')
          return
        }

        const { error } = await supabase.auth.signInWithOtp({ phone })
        if (error) throw error

        this.phoneState.lastPhoneE164 = phone
        this._cdStart('phoneOtp', phone)
        this.phoneState.success = this.$t('registerState.otpSent')

        this.otpModal.show = true
        this.otpModal.code = ''
        this.otpModal.error = ''
      } catch (e) {
        this.phoneState.error =
          this.translateSupabaseError(e?.message) ||
          this.$t('registerState.error')
      } finally {
        this.phoneState.sending = false
      }
    },

    // ===== Register (Phone OTP) Step 2: verify OTP =====
    async onVerifyPhoneOtp() {
      this.otpModal.error = ''
      this.otpModal.verifying = true

      try {
        const phone = this.otpModal.phoneE164 || this.phoneState.lastPhoneE164 || this.toE164Phone(this.phoneForm.phone)
        const token = (this.otpModal.code || '').trim()

        if (!phone || !phone.startsWith('+')) throw new Error(this.$t('loginState.invalidPhone'))
        if (!token) throw new Error(this.$t('member.otpRequired') || '請輸入驗證碼')

        const { error } = await supabase.auth.verifyOtp({
          type: 'sms',
          phone,
          token
        })
        if (error) throw error

        // ✅ A) 忘記密碼：直接登入
        if (this.otpModal.purpose === 'reset_login') {
          this.closeOtpModal()
          this.loginState.success = this.$t('loginState.success') || '登入成功'
          this.$router.replace('/app')
          return
        }

        // ✅ B) 註冊：維持你原本邏輯（驗證後設密碼）
        const p1 = (this.phoneForm.password || '').trim()
        if (p1 && p1.length >= 8) {
          const { error: upErr } = await supabase.auth.updateUser({ password: p1 })
          if (upErr) throw upErr
        }

        this.closeOtpModal()
        this.phoneForm = { phone: '', password: '', confirm: '' }
        this.activeTab = 'login'
        this.loginForm.identifier = phone
        this.loginState.success = this.$t('loginState.success')
        this.$router.replace('/app')
      } catch (e) {
        this.otpModal.error = this.translateSupabaseError(e?.message)
      } finally {
        this.otpModal.verifying = false
      }
    },

    // ===== resend OTP =====
    async onResendPhoneOtp() {
      this.otpModal.error = ''
      this.otpModal.resending = true
      try {
        const phone = this.otpModal.phoneE164 || this.phoneState.lastPhoneE164 || this.toE164Phone(this.phoneForm.phone)
        if (!phone || !phone.startsWith('+')) throw new Error(this.$t('loginState.invalidPhone'))

        const { error } = await supabase.auth.signInWithOtp({ phone })
        if (error) throw error

        this.phoneState.success = this.$t('loginState.resent')
        this._cdStart('phoneOtp', phone)
      } catch (e) {
        this.otpModal.error = this.translateSupabaseError(e?.message)
      } finally {
        this.otpModal.resending = false
      }
    },

    closeOtpModal() {
      this.otpModal.show = false
      this.otpModal.code = ''
      this.otpModal.error = ''
    },

    // ===== Forgot / Reset Password =====
    async onSendReset() {
      this.resetState.error = ''
      this.resetState.success = ''
      this.resetState.sending = true

      try {
        const idt = (this.loginForm.identifier || '').trim()
        if (!idt) throw new Error(this.$t('loginState.inputRequired') || '請輸入 Email 或手機')

        // ===== A) Email: 維持原流程 =====
        if (this.isEmail(idt)) {
          const redirectTo = `${window.location.origin}/member?flow=recovery`
          const { error } = await supabase.auth.resetPasswordForEmail(idt, { redirectTo })
          if (error) throw error

          this.resetState.success = this.$t('resetState.success')
          return
        }

        // ===== B) Phone: OTP 直接登入 =====
        const phone = this.toE164Phone(idt)
        if (!phone.startsWith('+')) throw new Error(this.$t('loginState.invalidPhone'))

        // （可選但建議）避免「未註冊手機」還送 OTP：用你現有的 RPC 反查
        // check_phone_available: true 表示可用(未被註冊)；reset 時我們要的是已存在 => available 應該是 false
        try {
          const { data: available, error: chkErr } = await supabase
            .rpc('check_phone_available', { p_phone: phone })
          if (chkErr) throw chkErr
          if (available) {
            // 安全做法：回「已送出」避免被拿來撞庫；你想要明確提示也行
            throw new Error(this.$t('resetState.phoneNotRegistered') || '此手機尚未註冊')
          }
        } catch (e) {
          // 如果你不想因 RPC 出錯而中斷，可改成 console.warn 然後繼續送 OTP
          // 這邊我採「嚴格」：出錯就丟
          if (e?.message) throw e
        }

        // 冷卻中就不送（沿用你 phoneOtp cooldown）
        if (this.phoneOtp?.secondsLeft > 0) return

        const { error } = await supabase.auth.signInWithOtp({ phone })
        if (error) throw error

        // 顯示提示（你也可以用 resetState.success）
        this.resetState.success = this.$t('resetState.otpSent') || '已發送簡訊驗證碼'

        // ✅ 打開 OTP Modal（purpose 設為 reset_login）
        this.otpModal.show = true
        this.otpModal.code = ''
        this.otpModal.error = ''
        this.otpModal.purpose = 'reset_login'
        this.otpModal.phoneE164 = phone

        this._cdStart('phoneOtp', phone)
      } catch (err) {
        this.resetState.error =
          (typeof err?.message === 'string' && err.message) || this.$t('resetState.error')
      } finally {
        this.resetState.sending = false
      }
    },

    async onConfirmNewPassword() {
      this.resetState.modalError = ''
      this.resetState.updating = true
      try {
        const p1 = (this.resetForm.password || '').trim()
        const p2 = (this.resetForm.confirm || '').trim()

        if (!p1 || p1.length < 8) {
          throw new Error(this.$t('passwordUpdate.minLength'))
        }
        if (p1 !== p2) {
          throw new Error(this.$t('passwordUpdate.mismatch'))
        }

        const { error } = await supabase.auth.updateUser({ password: p1 })
        if (error) throw error

        sessionStorage.removeItem('recovery_hold')
        if (typeof this.clearUrl === 'function') this.clearUrl()

        this.resetState.showModal = false
        this.resetForm.password = ''
        this.resetForm.confirm = ''

        // 顯示本地化成功訊息
        this.loginState.success = this.$t('passwordUpdate.successLogin')

        // 導回你的 App
        this.$router.replace('/app')
      } catch (e) {
        // 優先用後端訊息，否則用本地化預設錯誤
        this.resetState.modalError =
          (typeof e?.message === 'string' && e.message) || this.$t('passwordUpdate.error')
      } finally {
        this.resetState.updating = false
      }
    },

    closeResetModal() {
      sessionStorage.removeItem('recovery_hold')
      this.resetState.showModal = false
      this.resetState.modalError = ''
    },

    // ===== Handle redirect after email confirmation / recovery =====
    async handleEmailRedirectResult() {
      try {
        const q = new URLSearchParams(window.location.search)
        const h = new URLSearchParams((window.location.hash || '').replace(/^#/, ''))

        const errCode = q.get('error_code') || h.get('error_code')
        const errDesc = q.get('error_description') || h.get('error_description')
        const typeTag = (h.get('type') || q.get('type') || '').toLowerCase()
        const flowTag = (q.get('flow') || h.get('flow') || '').toLowerCase()

        const isRecoveryTag = typeTag === 'recovery' || flowTag === 'recovery'
        //const isSignupTag   = typeTag === 'signup'   || flowTag === 'signup'

        // ✅ 只有 recovery 才抓 token
        const tokenHash = isRecoveryTag ? this._getRecoveryTokenFromUrl(q, h) : ''



        const invalidLinkMsg = this.$t('resetState.invalidOrExpiredLink')

        // 有 error_code：優先處理恢復流程，否則顯示錯誤
        if (errCode) {
          const { data: sessA } = await supabase.auth.getSession()
          if (sessA?.session && (isRecoveryTag || tokenHash)) {
            sessionStorage.setItem('recovery_hold', '1')
            this.activeTab = 'login'
            this.resetState.showModal = true
            return true
          }
          this.activeTab = 'login'
          this.resetState.error = decodeURIComponent(errDesc || '') || invalidLinkMsg
          return true
        }

        // URL 上帶 token_hash（recovery 驗證）
        if (tokenHash) {
          try {
            const { error: vErr } = await supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash })
            if (vErr) throw vErr
            sessionStorage.setItem('recovery_hold', '1')
            this.activeTab = 'login'
            this.resetState.showModal = true
          } catch (e) {
            const { data: sessB } = await supabase.auth.getSession()
            if (sessB?.session) {
              sessionStorage.setItem('recovery_hold', '1')
              this.activeTab = 'login'
              this.resetState.showModal = true
            } else {
              this.resetState.error = (typeof e?.message === 'string' && e.message) || invalidLinkMsg
            }
          }
          return true
        }

        // 僅有 type=recovery
        if (isRecoveryTag) {
          sessionStorage.setItem('recovery_hold', '1')
          this.activeTab = 'login'
          this.resetState.showModal = true
          return true
        }

        // 信箱驗證成功就直接進 App
        const { data: userRes } = await supabase.auth.getUser()
        if (!isRecoveryTag && userRes?.user?.email_confirmed_at) {
          this.$router.replace('/app')
          return true
        }

        // 其他錯誤文字（例如 provider 回傳），顯示原文；沒有就用你的通用錯誤
        const errHash = h.get('error_description')
        if (errHash) {
          this.activeTab = 'login'
          this.loginState.error = decodeURIComponent(errHash) || this.$t('loginState.errorTryLater')
          return true
        }

        return false
      } catch {
        return false
      }
    },

    _buildRedirectTo(baseUrl, extra = {}) {
      const u = new URL(baseUrl, window.location.origin)
      Object.entries(extra).forEach(([k, v]) => u.searchParams.set(k, String(v)))
      return u.toString()
    },

    _getRecoveryTokenFromUrl(q, h) {
      return (
        q.get('token_hash') || q.get('code') || q.get('token') ||
        h.get('token_hash') || h.get('code') || h.get('token') || ''
      )
    },

    openSuccessModal(msg) {
      this.loginState.success = msg || ''
      this.successModal.show = !!this.loginState.success
    },

    closeSuccessModal() {
      this.successModal.show = false
      this.activeTab = 'login'
    },

    clearUrl() { history.replaceState(null, document.title, window.location.pathname) }
  }
}
</script>

<style scoped>
.member-page {
  position: relative;
  min-height: 100vh;
  background: url('~@/assets/images/TSN_Available-sizes_bg.png') no-repeat center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.form-wrapper {
  width: 100%;
  max-width: 400px;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
}

.decor {
  display: none;
}

/* ===== 桌機版：分 1/3 + 2/3 ===== */
@media (min-width: 900px) {
  .member-page {
    display: grid;
    grid-template-columns: 1fr 2fr;   /* 左 1/3、右 2/3 */
    align-items: stretch;             /* 兩欄上下貼齊 */
    min-height: 100svh;
    padding: 0;
  }

  /* 左側白底：中間 50% 高度 */
  .form-wrapper {
    max-width: none;
    width: 100%;
    height: 50vh;                     /* 垂直只佔 50% 視窗高 */
    margin: auto 0;                   /* 垂直置中 */
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 2rem 2.5rem;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 12px;
  }

  /* 右側主視覺：左右貼齊，上下可裁切 */
  .decor {
    display: block;
    position: relative;
    height: 100svh;                   /* 撐滿整個視窗高度 */
    overflow: hidden;
  }

  .decor img {
    position: absolute;
    inset: 0;                         /* 貼滿容器 */
    width: 100%;
    height: 100%;
    object-fit: cover;                /* 填滿比例，多的裁掉 */
    object-position: center;
    display: block;
  }
}

.register-switch{
  display: flex;
  width: 100%;
}

.register-switch > .btn{
  flex: 1 1 0;
  white-space: nowrap;
}

/* 想保留 btn-group 那種貼合感（不要中間雙邊框） */
.register-switch > .btn + .btn{
  margin-left: -1px;
}


/* Reset 密碼彈窗樣式 */
.reset-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1000;
}
.reset-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0,0,0,.18);
  padding: 18px;
}
.verified-check {
  position: absolute;
  right: -6px;
  top: 34px;
  background: #22c55e;
  color: #fff;
  border-radius: 999px;
  width: 22px;
  height: 22px;
  display: inline-grid;
  place-items: center;
  font-weight: 700;
  font-size: 14px;
}

/* placeholder 顏色 */
.form-control::placeholder {
  color: #9ca3af;
  opacity: 1;
}

/* 底部提示條 */
.legal-foot {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: max(12px, env(safe-area-inset-bottom, 0px));
  font-size: 0.92rem;
  color: #111;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 12px;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0,0,0,.12);
  backdrop-filter: blur(6px);
  line-height: 1.6;
  text-align: center;
  z-index: 2000;
  white-space: nowrap;
}
.legal-link {
  color: #0f62fe;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.legal-link:active {
  opacity: .85;
}

/* 小螢幕：允許換行 */
@media (max-width: 420px) {
  .legal-foot {
    font-size: 0.88rem;
    padding: 6px 10px;
    white-space: normal;
    max-width: calc(100vw - 24px);
  }
}

.otp-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1100;
}
.otp-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0,0,0,.18);
  padding: 18px;
}

.success-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1200;
}
.success-card {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 34px rgba(0,0,0,.22);
  padding: 18px;
}
.success-text {
  font-size: 1.02rem;
  line-height: 1.6;
  word-break: break-word;
}

/* 使用條款彈窗 */
.terms-modal{
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: grid;
  place-items: center;
  padding: 18px;
  z-index: 1300;
}

.terms-card{
  width: min(820px, calc(100vw - 36px));
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 34px rgba(0,0,0,.22);
  padding: 18px 18px 16px;
}

/* 標題置中 */
.terms-title{
  text-align: center;
  font-weight: 800;
  font-size: 1.25rem;
  margin: 4px 0 12px;
}

/* 藍色框框（跟示意圖一樣的味道） */
.terms-box{
  border: 2px solid var(--bs-primary);
  border-radius: 12px;
  padding: 12px 14px;
  max-height: 220px;
  overflow: auto;
}

.terms-content{
  font-size: .95rem;
  line-height: 1.7;
  color: #111;
}

/* 勾選區 */
.terms-checks{
  margin-top: 12px;
  text-align: left;      /* 強制文字靠左 */
  align-self: stretch;   /* 如果父層是 flex，讓它撐滿寬度 */
  width: 100%;
}

.chk{
  display: flex;
  gap: 10px;
  align-items: flex-start;
  justify-content: flex-start;  /* 明確靠左 */
  margin: 10px 0;
}

.chk input{
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

/* ✅ 兩顆按鈕置中，左右對分 */
.terms-actions{
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  justify-content: center;
}

.terms-btn{
  width: 100%;
  height: 44px;
  border-radius: 10px;
  font-weight: 700;
}

/* 取消按鈕灰色（接近示意圖） */
.btn-secondary{
  background: #c9c9c9;
  border-color: #c9c9c9;
  color: #222;
}
.btn-secondary:hover{
  filter: brightness(.97);
}

/* 確認 disabled 的視覺：更像「不能按」 */
.terms-btn:disabled{
  opacity: .55;
  cursor: not-allowed;
}

/* ✅ 強制條款內容左對齊（v-html 內容要用 deep 才能吃到） */
.terms-box ::v-deep(*) {
  text-align: left !important;
}

/* 讓 Markdown 標題在 Modal 裡不要太巨 */
.terms-box ::v-deep(h1) {
  font-size: 1.05rem;
  font-weight: 800;
  margin: 0 0 10px;
}
.terms-box ::v-deep(h2) {
  font-size: 1.02rem;
  font-weight: 800;
  margin: 14px 0 8px;
}
.terms-box ::v-deep(h3),
.terms-box ::v-deep(h4) {
  font-size: 0.98rem;
  font-weight: 800;
  margin: 12px 0 6px;
}
.terms-box ::v-deep(h5) {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 10px 0 6px;
}

/* 文字與列表舒服一點 */
.terms-box ::v-deep(p),
.terms-box ::v-deep(li) {
  line-height: 1.75;
  color: #222;
  margin: 0 0 8px;
}

.terms-box ::v-deep(ul),
.terms-box ::v-deep(ol) {
  padding-left: 1.2em;
  margin: 0 0 10px;
}

.terms-box ::v-deep(hr) {
  border: 0;
  border-top: 1px solid rgba(0,0,0,.12);
  margin: 12px 0;
}

</style>
